import { Switch } from '@/components/ui/switch';
import { useUpdateAgentSettingsAction } from '@/hooks/actions/use-update-agent-settings-action';
import { useAgentSettingsQuery } from '@/hooks/queries/use-agent-settings-query';
import { Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { AgentSlug } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/** User-visible agent toggles. prefs-extractor is internal plumbing and is excluded. */
const USER_AGENTS: Array<{ slug: AgentSlug; titleKey: string; descriptionKey: string }> = [
  {
    slug: AgentSlug.BRAND_MATCH,
    titleKey: 'settings.agents.brandMatch.title',
    descriptionKey: 'settings.agents.brandMatch.description',
  },
];

type AgentTogglesSectionProps = {
  brandId: string;
};

/**
 * Toggle list for user-visible agents. Extracted so the Settings Companion IA
 * page can compose it alongside the Brand DNA summary and preferences editor.
 */
const AgentTogglesSection = ({ brandId }: AgentTogglesSectionProps) => {
  const { t } = useTranslation();
  const { data: settings, isLoading } = useAgentSettingsQuery(brandId);
  const updateAction = useUpdateAgentSettingsAction(brandId);

  if (isLoading || !settings) {
    return (
      <Flex justify="center" py={6}>
        <Spinner />
      </Flex>
    );
  }

  const enabledAgents = settings.enabledAgents;

  const handleToggle = (slug: AgentSlug, nextChecked: boolean) => {
    const next = nextChecked
      ? Array.from(new Set([...settings.enabledAgents, slug]))
      : settings.enabledAgents.filter((s) => s !== slug);
    updateAction.mutate({ enabledAgents: next });
  };

  return (
    <VStack align="stretch" gap={4}>
      {USER_AGENTS.map((agent) => {
        const checked = enabledAgents.includes(agent.slug);
        return (
          <Flex key={agent.slug} justify="space-between" align="center" gap={4}>
            <VStack align="start" gap={1} flex={1}>
              <Text fontWeight="semibold">{t(agent.titleKey)}</Text>
              <Text color="fg.muted" fontSize="sm">
                {t(agent.descriptionKey)}
              </Text>
            </VStack>
            <Switch
              checked={checked}
              disabled={updateAction.isPending}
              onCheckedChange={(details) => handleToggle(agent.slug, details.checked)}
            />
          </Flex>
        );
      })}
    </VStack>
  );
};

export default AgentTogglesSection;
